import {
  getSignedUrlForGet,
  IDocumentAssemblerTemplateVersionDetail,
} from '@commenda/api';
import {
  Alert,
  Button,
  Icons,
  PreviewDownloadDialog,
  useToggle,
} from '@commenda/kakashi';
import { downloadFileFromLink } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { ConfirmationModal } from '../molecules/modals/ConfirmationModal';

import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

interface DocumentAssemblerVersionFileCardProps {
  templateKey: string;
  version: IDocumentAssemblerTemplateVersionDetail;
  isEditable: boolean;
  onFileReplace: () => void;
}

interface VersionFileActionsProps {
  fileId: number;
  fileName: string;
  extraActions?: React.ReactNode;
}

/** View and download for one stored file, resolving a fresh signed URL per action. */
const VersionFileActions = ({
  fileId,
  fileName,
  extraActions,
}: VersionFileActionsProps) => {
  const { setNotification } = useNotification();
  const previewModalVisibility = useToggle(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOpeningPreview, setIsOpeningPreview] = useState(false);
  const [previewSignedUrl, setPreviewSignedUrl] = useState<string | null>(null);

  const onSignedUrlFailure = () => {
    setNotification({
      show: true,
      valid: false,
      title: 'The file could not be opened',
      message: 'Try again in a moment.',
    });
  };

  // downloadFileFromFileId swallows a null signed URL into a console.error, which leaves the button
  // doing nothing at all. Resolving the URL here makes the failure visible.
  const onFileDownload = async () => {
    setIsDownloading(true);
    const result = await getSignedUrlForGet(fileId);
    setIsDownloading(false);

    if (!result?.signedUrl) {
      onSignedUrlFailure();
      return;
    }

    downloadFileFromLink(result.signedUrl, fileName);
  };

  const onFileView = async () => {
    setIsOpeningPreview(true);
    const result = await getSignedUrlForGet(fileId);
    setIsOpeningPreview(false);

    if (!result?.signedUrl) {
      onSignedUrlFailure();
      return;
    }

    // Resolved per open rather than held: a signed URL expires, and a stale one renders an empty
    // viewer with nothing to say why.
    setPreviewSignedUrl(result.signedUrl);
    previewModalVisibility.on();
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-md">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icons.Eye className="size-4" />}
          isLoading={isOpeningPreview}
          onClick={onFileView}
        >
          View
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icons.Download01 className="size-4" />}
          isLoading={isDownloading}
          onClick={onFileDownload}
        >
          Download
        </Button>
        {extraActions}
      </div>

      {previewModalVisibility.value && previewSignedUrl ? (
        <PreviewDownloadDialog
          isOpen
          onClose={previewModalVisibility.off}
          previewFile={{ fileName, signedURL: previewSignedUrl }}
        />
      ) : null}
    </>
  );
};

export const DocumentAssemblerVersionFileCard = ({
  templateKey,
  version,
  isEditable,
  onFileReplace,
}: DocumentAssemblerVersionFileCardProps) => {
  const replaceConfirmModalVisibility = useToggle(false);

  const fileName = DocumentAssemblerHelper.buildVersionFileName(
    templateKey,
    version.version
  );

  return (
    <>
      <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
        <div className="flex flex-col gap-xs">
          <h3 className="txt-medium-medium text-text-primary">The Word file</h3>
          <p className="txt-extra-small-regular text-text-secondary">
            Everything this version is, apart from its writing conventions.
          </p>
        </div>

        {isEditable ? (
          <Alert
            variant="warning"
            title="Replacing the file re-reads it"
            description="The problems found in it, and whether this version can be used, are recomputed from the new file. Your writing conventions are not touched."
          />
        ) : null}

        <VersionFileActions
          fileId={version.sourceFileId}
          fileName={fileName}
          extraActions={
            isEditable ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={replaceConfirmModalVisibility.on}
              >
                Replace file
              </Button>
            ) : undefined
          }
        />

        {version.originalDocumentFileId !== null ? (
          <div className="flex flex-col gap-md border-t border-border-primary pt-lg">
            <div className="flex flex-col gap-xs">
              <h4 className="txt-small-medium text-text-primary">
                Original document
              </h4>
              <p className="txt-extra-small-regular text-text-secondary">
                The document this version was created from, exactly as it was
                attached. The Word file above is the tagged template the engine
                fills.
              </p>
            </div>
            <VersionFileActions
              fileId={version.originalDocumentFileId}
              fileName={DocumentAssemblerHelper.buildVersionOriginalDocumentFileName(
                templateKey,
                version.version
              )}
            />
          </div>
        ) : null}
      </div>

      <ConfirmationModal
        isOpen={replaceConfirmModalVisibility.value}
        onClose={replaceConfirmModalVisibility.off}
        onConfirm={() => {
          replaceConfirmModalVisibility.off();
          onFileReplace();
        }}
        title="Replace this version’s file"
        message="The new file is read immediately. The problem list, and whether this version can be used, are recomputed from it. Your writing conventions are left untouched."
        confirmButtonText="Choose a new file"
      />
    </>
  );
};
